import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Loader2 } from 'lucide-react';
import { useLenis } from '../../hooks/useLenis';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';
import { SubscriptionCards } from '../../components/profile/SubscriptionCards';
import {
  getAvailablePlans,
  getSubscriptionStatus,
  getActiveDiscount,
  cancelSubscription,
  reactivateSubscription,
  cancelPendingDowngrade,
  type SubscriptionPlan,
  type SubscriptionStatusResponse,
  type ActiveDiscount,
} from '../../services/payment/subscriptionService';
import fcmsLimitsService, { type FcmsFeatureLimits } from '../../services/fcms/fcmsLimitsService';
import { invalidateFeatureAccessCache } from '../../hooks/useFeatureAccess';
import { PlanChangeDialog } from '../../components/profile/PlanChangeDialog';
import toast from '../../utils/toast';

/**
 * Pflicht-Abo-Auswahl-Seite.
 *
 * Wird durch ProtectedRoute angesteuert, wenn ein authentifizierter User noch
 * keine aktive zahlpflichtige Subscription in einer der Pflicht-Familien hat.
 *
 * Visuell identisch zum Abonnement-Tab im Profil — nutzt dieselbe SubscriptionCards-
 * Komponente, damit Plan-Karten, Pricing, Badges, Toggle (monatlich/jährlich) und
 * Layout konsistent sind.
 */
export const SubscriptionRequiredPageTW: React.FC = () => {
  useLenis();
  const navigate = useNavigate();
  const { user, logout, refreshSubscriptions } = useAuth();
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  // Lokaler State analog zur ProfilePage -- selbe Datenquellen
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [activeDiscountData, setActiveDiscountData] = useState<ActiveDiscount | null>(null);
  const [fcmsLimits, setFcmsLimits] = useState<FcmsFeatureLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState<string | null>(null);
  const [reactivatingSubscription, setReactivatingSubscription] = useState<string | null>(null);

  // PlanChange-Dialog (z.B. Wechsel zwischen Bitcoin-Stacker ↔ Bitcoin-Whale)
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false);
  const [planChangeTargetSlug, setPlanChangeTargetSlug] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansData, statusData, discountData] = await Promise.all([
        getAvailablePlans(),
        getSubscriptionStatus(),
        getActiveDiscount(),
      ]);
      setPlans(plansData);
      setSubscriptionStatus(statusData);
      setActiveDiscountData(discountData);
      try {
        const limits = await fcmsLimitsService.getLimits();
        setFcmsLimits(limits);
      } catch {
        setFcmsLimits(null);
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh AuthContext-State -- damit ProtectedRoute nach Webhook-getriebenem
    // Status-Update den User vom Pflicht-Gate freischaltet.
    refreshSubscriptions();
  }, [refreshSubscriptions]);

  const handleSubscribe = (planSlug: string, interval: 'monthly' | 'yearly' = 'monthly') => {
    const params = new URLSearchParams({ plan: planSlug });
    if (interval === 'yearly') params.set('interval', 'yearly');
    navigate(`/checkout?${params.toString()}`);
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    setCancellingSubscription(subscriptionId);
    try {
      await cancelSubscription(subscriptionId);
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Abonnement wurde gekündigt.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Kündigen des Abonnements');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleReactivateSubscription = async (subscriptionId: string) => {
    setReactivatingSubscription(subscriptionId);
    try {
      await reactivateSubscription(subscriptionId);
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Abonnement wurde reaktiviert.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Reaktivieren des Abonnements');
    } finally {
      setReactivatingSubscription(null);
    }
  };

  const handlePlanChange = (targetPlanSlug: string) => {
    setPlanChangeTargetSlug(targetPlanSlug);
    setPlanChangeDialogOpen(true);
  };

  const handlePlanChangeSuccess = async () => {
    setPlanChangeDialogOpen(false);
    invalidateFeatureAccessCache();
    await Promise.all([fetchData(), refreshSubscriptions()]);
  };

  const handleCancelPendingDowngrade = async () => {
    try {
      await cancelPendingDowngrade();
      invalidateFeatureAccessCache();
      await Promise.all([fetchData(), refreshSubscriptions()]);
      toast.success('Geplanter Plan-Wechsel wurde storniert.');
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Stornieren');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" aria-label="Zur Startseite" className="inline-block">
            <img alt="RENDITEFUCHS" src={logoUrl} className="mx-auto h-10 w-auto" />
          </Link>
          <h1 className="mt-8 text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            {user?.firstName ? `Willkommen, ${user.firstName}!` : 'Willkommen bei RENDITEFUCHS!'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Starte jetzt mit deinem persönlichen Bereich. Wähle mindestens einen Modulbereich
            — du kannst später jederzeit weitere ergänzen.
          </p>
        </div>

        {/* Subscription Cards — identisch zur Profil-Ansicht /profil/abonnement */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-brand animate-spin" />
          </div>
        ) : (
          <SubscriptionCards
            plans={plans}
            subscriptions={subscriptionStatus?.subscriptions || []}
            fcmsLimits={fcmsLimits}
            activeDiscount={activeDiscountData}
            onSubscribe={handleSubscribe}
            onCancelSubscription={handleCancelSubscription}
            onReactivateSubscription={handleReactivateSubscription}
            onPlanChange={handlePlanChange}
            onCancelPendingDowngrade={handleCancelPendingDowngrade}
            cancellingSubscription={cancellingSubscription}
            reactivatingSubscription={reactivatingSubscription}
          />
        )}

        {/* Footer: Logout + Hinweis */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200 dark:border-gray-800">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left">
            Du kannst dein Abo jederzeit zum Ende der laufenden Periode kündigen.
            Es gibt keine Mindestlaufzeit.
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Abmelden
          </button>
        </div>

        {/* Plan-Wechsel-Dialog (Upgrade Stacker -> Whale o.ae.) */}
        {planChangeDialogOpen && (
          <PlanChangeDialog
            isOpen={planChangeDialogOpen}
            onClose={() => setPlanChangeDialogOpen(false)}
            targetPlanSlug={planChangeTargetSlug}
            onSuccess={handlePlanChangeSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionRequiredPageTW;
